// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import ChevronRightSolid from "@werner/XDL/Icons/ChevronRightSolid";
import { buttonTransparent } from "@werner/XDL/PreStyle/button";
import color from "@werner/XDL/Utilities/color";
import { createLinkIconStyles } from "@werner/XDL/Utilities/sharedStyles";
import * as React from "react";
import { css } from "styled-components";

export const generateHoverStyleForLink = (hoverColor = "primary", disabledColor = "common.ui3") => css`
    &:hover:not(:disabled) {
        & > ${TypographyStyle}, & > ${IconWrapper} {
            color: ${color(hoverColor)};
        }
    }
    &:disabled {
        & > ${TypographyStyle}, & > ${IconWrapper} {
            color: ${color(disabledColor, 0.5)};
        }
    }
`;

export const linkArrowStyle: LinkPresentationProps = {
    ...createLinkIconStyles(ChevronRightSolid, {
        color: "common.ui3",
        icon: {
            iconProps: {
                size: 7,
                css: css`
                    margin-left: 4px;
                `,
            },
            position: "right",
        },
        typographyProps: {
            size: 16,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 30px;
            `,
        },

        css: css`
            ${generateHoverStyleForLink()}
        `,
    }),
};

interface IconButtonProps {
    typographyProps?: TypographyProps;
}

export const iconButtonCommonStyles = (
    iconSrc: React.ComponentType | string,
    iconSize?: number,
    buttonProps?: IconButtonProps,
    iconColor?: string,
) => {
    return {
        mergeCss: true,
        icon: {
            src: iconSrc,
            position: "left",
        },
        typographyProps: {
            color: "secondary",
            size: 13,
            weight: "bold",
            transform: "capitalize",
            ...buttonProps?.typographyProps,
            css: css`
                ${buttonProps?.typographyProps?.css}
                letter-spacing: 0;
                line-height: 14px;
                &:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        css: css`
            padding: 0;
            ${buttonTransparent}
            ${IconWrapper} {
                top: 0;
                margin-right: 5px;
                color: ${color(iconColor) || color("secondary")};
                &,
                & svg {
                    width: ${iconSize || 14}px;
                    height: ${iconSize || 14}px;
                }
            }

            &:hover:not([disabled]),
            &:focus:not([disabled]),
            &:focus:hover {
                &,
                & ${IconWrapper} {
                    color: ${color("text.link")};
                }
            }

            &:disabled {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("secondary", 0.5)};
                    background-color: transparent;
                }
            }

            &,
            * {
                font-size: ${`${buttonProps?.typographyProps?.size}px` || "13px"};
                font-weight: ${buttonProps?.typographyProps?.weight || "600"};
            }
        `,
    } as ButtonPresentationProps;
};

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        color: "primary",
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                color: "primary",
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 14,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 12px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                &,
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("primary", 0.5)};
                }
            }
            &:hover {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("common.ui3")};
                }
                & ${IconWrapper} {
                    opacity: 1;
                }
            }
        `,
    } as LinkPresentationProps;
};
